import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { Site } from "../types/Site";
import { authService } from "../services/Services";
import { useFetchSite } from "../hooks/useFetchSites";
import { queryClient } from "../react-query/queryClient";

export type UserAnnotation = "internal" | "external" | null;

export interface ITimezone {
    timezone: string;
    ianaName: string;
}

export interface IUserDetails {
    username: string | null;
    annotation: UserAnnotation;
}

export interface UserContextType {
    userDetails: IUserDetails | null;
    setUserDetails: (userDetails: IUserDetails | null) => void;
    allowedSites: Site[];
    selectedSite: Site | null;
    setSelectedSite: (site: Site | null) => void;
    isReady: boolean;
    timezoneDetails: ITimezone;

    getUserAnnotation: () => UserAnnotation;
    isInternalUser: () => boolean;
    isExternalUser: () => boolean;
    isVideoModalEnabled: () => boolean;
}

interface Props {
    setErrorState(error: any): void;
}

const UserContext = createContext<UserContextType | null>(null);

const UserProvider: React.FC<PropsWithChildren<Props>> = ({ children, setErrorState }) => {
    const { data: sites, error, isError, isLoading: isSitesLoading } = useFetchSite();
    const [userDetails, setUserDetails] = useState<IUserDetails | null>(
        authService.getIdentityToken()
            ? {
                  username: authService.getUsername(),
                  annotation: null,
              }
            : null,
    );
    const [allowedSites, setAllowedSites] = useState<Site[]>([]);
    const [selectedSite, setSelectedSite] = useState<Site | null>(null);
    const [videoModalEnabled, setVideoModalEnabled] = useState(false);

    useEffect(() => {
        const loadUserAnnotation = async () => {
            if (userDetails?.username && authService.getIdentityToken()) {
                try {
                    const annotation = await authService.getUserAnnotation();
                    setUserDetails((prevDetail) => {
                        if (!prevDetail) return null;
                        return {
                            ...prevDetail,
                            annotation: annotation as UserAnnotation,
                        };
                    });
                    // Check for custom:video-modal claim
                    const claims = await authService.getUserCustomAttribute("custom:video_modal");
                    if (claims === "true") {
                        setVideoModalEnabled(true);
                    } else {
                        setVideoModalEnabled(false);
                    }
                    // Check for custom:bounding-boxes claim
                    const bbClaims = await authService.getUserCustomAttribute("custom:bounding_boxes");
                } catch (error) {
                    console.error("Error loading annotation");
                }
            }
        };
        loadUserAnnotation();
    }, [userDetails?.username]);

    useEffect(() => {
        if (userDetails === null) {
            setAllowedSites([]);
            setSelectedSite(null);
        }
        if (!userDetails) {
            queryClient.setQueryData(["sites"], []);
            queryClient.invalidateQueries();
            setAllowedSites([]);
            setSelectedSite(null);
        }

        if (isError) {
            setErrorState({
                hasError: true,
                message: "unable to load Sites",
            });
            setAllowedSites([]);
            setSelectedSite(null);
        } else if (sites) {
            setAllowedSites(sites);
            const savedSiteName = localStorage.getItem("selected_site");
            let siteToSelect: Site | null = null;

            if (savedSiteName) {
                // Try to find the saved site
                siteToSelect = sites.find((site) => site.displayName === savedSiteName) || null;
            }

            // If no saved site or saved site not found, use the first site
            if (!siteToSelect && sites.length > 0) {
                siteToSelect = sites[0];
                localStorage.setItem("selected_site", siteToSelect.displayName);
            }

            setSelectedSite(siteToSelect);
        }
    }, [userDetails, setErrorState, sites, error]);

    useEffect(() => {
        if (selectedSite) {
            localStorage.setItem("selected_site", selectedSite.displayName);
        }
    }, [selectedSite]);

    useEffect(() => {
        if (!(userDetails && userDetails != null)) {
            setAllowedSites([]);
            setSelectedSite(null);
        }
    }, [userDetails, setErrorState]);

    const getUserAnnotation = useCallback((): UserAnnotation => {
        return userDetails?.annotation || null;
    }, [userDetails]);

    const isInternalUser = useCallback((): boolean => {
        return getUserAnnotation() === "internal";
    }, [getUserAnnotation]);

    const isExternalUser = useCallback((): boolean => {
        return getUserAnnotation() === "external";
    }, [getUserAnnotation]);

    const isVideoModalEnabled = useCallback((): boolean => videoModalEnabled, [videoModalEnabled]);

    const timezone = useMemo<string>(() => {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }, []);

    const timezoneAbbreviation = useMemo<string>(() => {
        const date = new Date();
        const formatter = new Intl.DateTimeFormat("en-US", {
            timeZone: timezone,
            timeZoneName: "short",
        });
        const parts = formatter.formatToParts(date);
        const tzPart = parts.find((part) => part.type === "timeZoneName");
        return tzPart?.value ?? "";
    }, [timezone]);

    const contextValue = useMemo<UserContextType>(() => {
        return {
            userDetails,
            setUserDetails,
            allowedSites,
            selectedSite,
            setSelectedSite,
            isReady: !isSitesLoading && allowedSites.length > 0,
            timezoneDetails: {
                timezone: timezone,
                ianaName: timezoneAbbreviation,
            },
            getUserAnnotation,
            isInternalUser,
            isExternalUser,
            isVideoModalEnabled,
        };
    }, [
        userDetails,
        allowedSites,
        selectedSite,
        timezone,
        timezoneAbbreviation,
        getUserAnnotation,
        isInternalUser,
        isExternalUser,
        isVideoModalEnabled,
    ]);

    return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

export { UserProvider, UserContext };
